import React, { useState } from 'react';
import '../Styles/globals.css';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import LookalLogo from '../Images/Lookal Logo.png';

const Auth: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true); // Default to true for better UX
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupBusiness, setSignupBusiness] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupReferralCode, setSignupReferralCode] = useState('');
  const [loadingAction, setLoadingAction] = useState<'google' | 'login' | 'magic-link' | 'reset' | 'signup' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [resetPasswordSent, setResetPasswordSent] = useState(false);
  const [loginSuccessful, setLoginSuccessful] = useState(false);
  const loading = loadingAction !== null;
  
  const authContext = useAuth();
  const { sendMagicLink, signIn, signInWithGoogle, signUp, resetPassword, user, loading: authLoading } = authContext || {};
  const redirectStatePath = (location.state as { redirect?: string } | null)?.redirect;
  const redirectQueryPath = new URLSearchParams(location.search).get('redirect') || new URLSearchParams(location.search).get('next');
  const redirectPath = (() => {
    const candidate = redirectStatePath && redirectStatePath !== '/' ? redirectStatePath : redirectQueryPath || '/';
    return candidate.startsWith('/') ? candidate : '/';
  })();

  // Auto-redirect when user is authenticated after login
  React.useEffect(() => {
    if (loginSuccessful && user && !authLoading) {
      navigate(redirectPath);
    }
  }, [user, authLoading, loginSuccessful, navigate, redirectPath]);

  if (!authContext) {
    return <div>Loading authentication...</div>;
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingAction('login');
    setError(null);
    setMagicLinkSent(false);
    setResetPasswordSent(false);
    
    try {
      if (!loginEmail || !loginPassword) {
        setError('Please enter both email and password');
        return;
      }

      const { data, error } = await signIn(loginEmail, loginPassword, rememberMe);
      if (error) {
        // Handle specific Supabase errors using error codes (best practice per Supabase docs)
        if (error.code === 'auth_timeout') {
          setError('Login request timed out. Please check your internet connection and try again.');
        } else if (error.code === 'invalid_credentials' || error.message?.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please try again.');
        } else if (error.code === 'email_not_confirmed' || error.message?.includes('Email not confirmed')) {
          setError('Please check your email and click the confirmation link before signing in.');
        } else if (error.code === 'over_request_rate_limit' || error.message?.includes('Too many requests')) {
          setError('Too many login attempts. Please wait a moment and try again.');
        } else {
          setError(error.message || 'Login failed. Please try again.');
        }
      } else if (data?.session) {
        // Clear logout parameter from URL if present
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('logout')) {
          urlParams.delete('logout');
          const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
          window.history.replaceState({}, '', newUrl);
        }

        // Set flag to trigger navigation via useEffect once user state is updated
        setLoginSuccessful(true);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleForgotPassword = async () => {
    if (!loginEmail) {
      setError('Please enter your email address first');
      return;
    }

    setLoadingAction('reset');
    setError(null);
    setMagicLinkSent(false);
    setResetPasswordSent(false);
    
    try {
      const { error } = await resetPassword(loginEmail);
      if (error) {
        if (error.code === 'auth_timeout') {
          setError('Password reset request timed out. Please check your internet connection and try again.');
        } else if (error.message?.includes('rate limit')) {
          setError('Too many password reset requests. Please wait a moment and try again.');
        } else if (error.message?.includes('Invalid email')) {
          setError('Please enter a valid email address.');
        } else {
          setError(error.message || 'Failed to send reset email. Please try again.');
        }
      } else {
        setResetPasswordSent(true);
        setError(null);
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleMagicLink = async () => {
    if (!loginEmail) {
      setError('Please enter your email address first');
      return;
    }

    setLoadingAction('magic-link');
    setError(null);
    setMagicLinkSent(false);
    setResetPasswordSent(false);

    try {
      const { error } = await sendMagicLink(loginEmail, redirectPath);

      if (error) {
        if (error.code === 'auth_timeout') {
          setError('Magic link request timed out. Please check your internet connection and try again.');
        } else if (error.message?.includes('Email not confirmed')) {
          setError('Please confirm your email address before using a magic link.');
        } else if (error.message?.includes('User not found')) {
          setError('No account was found for this email. Please sign up first.');
        } else if (error.message?.includes('rate limit') || error.message?.includes('Too many requests')) {
          setError('Too many magic link requests. Please wait a moment and try again.');
        } else {
          setError(error.message || 'Failed to send magic link. Please try again.');
        }
      } else {
        setMagicLinkSent(true);
      }
    } catch (err) {
      console.error('Magic link error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoadingAction('google');
    setError(null);
    setMagicLinkSent(false);
    setResetPasswordSent(false);

    try {
      const { data, error } = await signInWithGoogle(redirectPath);

      if (error) {
        setError(error.message || 'Failed to start Google Sign-In. Please try again.');
        setLoadingAction(null);
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      setError('Failed to start Google Sign-In. Please try again.');
      setLoadingAction(null);
    } catch (err) {
      console.error('Google Sign-In error:', err);
      setError('An unexpected error occurred. Please try again.');
      setLoadingAction(null);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingAction('signup');
    setError(null);
    setMagicLinkSent(false);
    setSignupSuccess(false);
    
    try {
      if (!signupName || !signupEmail || !signupPassword) {
        setError('Please fill all required fields');
        return;
      }

      // Basic password validation
      if (signupPassword.length < 6) {
        setError('Password must be at least 6 characters long');
        return;
      }

      const { data, error } = await signUp(signupEmail, signupPassword, {
        name: signupName,
        businessName: signupBusiness,
        phone: signupPhone,
        referred_by: signupReferralCode.trim() || null
      });
      
      if (error) {
        // Handle specific Supabase errors using error codes (best practice per Supabase docs)
        if (error.code === 'auth_timeout') {
          setError('Signup request timed out. Please check your internet connection and try again.');
        } else if (error.message?.includes('User already registered')) {
          setError('An account with this email already exists. Please try logging in instead.');
        } else if (error.message?.includes('Invalid email')) {
          setError('Please enter a valid email address.');
        } else if (error.message?.includes('Password should be at least')) {
          setError('Password must be at least 6 characters long.');
        } else if (error.message?.includes('Email rate limit exceeded')) {
          setError('Too many signup attempts. Please try again later.');
        } else {
          setError(error.message || 'Failed to create account. Please try again.');
        }
      } else {
        setSignupSuccess(true);
        
        // Check if email confirmation is required
        if (data.user && !data.user.email_confirmed_at) {
          setError(null); // Clear any previous errors
          // Don't auto-redirect, let user confirm email first
        } else {
          // Email confirmed or confirmation not required, proceed
          // No need to call onAuthSuccess as useSupabase will handle the state change
        }
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-2xl sm:rounded-[2.5rem] shadow border border-gray-300 px-6 sm:px-10 py-6 sm:py-8 w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl flex flex-col items-center">
        <div className="flex flex-col items-center mb-2 select-none">
          <img
            src={LookalLogo}
            alt="LooKal"
            className="h-12 sm:h-16 w-auto"
          />
        </div>
        <div className="flex w-full justify-center mt-2 mb-6 sm:mb-7">
          <button
            className={`mx-2 sm:mx-4 pb-1 text-base sm:text-lg font-semibold border-b-2 transition-colors duration-200 ${activeTab === 'login' ? 'text-black border-black' : 'text-gray-400 border-transparent'}`}
            onClick={() => { setActiveTab('login'); setError(null); setResetPasswordSent(false); }}
            type="button"
          >
            Login
          </button>
          <button
            className={`mx-2 sm:mx-4 pb-1 text-base sm:text-lg font-semibold border-b-2 transition-colors duration-200 ${activeTab === 'signup' ? 'text-black border-black' : 'text-gray-400 border-transparent'}`}
            onClick={() => { setActiveTab('signup'); setError(null); setSignupSuccess(false); setResetPasswordSent(false); }}
            type="button"
          >
            Sign Up
          </button>
        </div>
        {activeTab === 'login' && (
          <form className="w-full flex flex-col" onSubmit={handleLogin}>
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-xl">{error}</div>
            )}
            <label className="text-sm sm:text-base font-semibold mb-1 mt-2 text-gray-800">Email</label>
            <div className="relative mb-3">
              <input
                type="email"
                placeholder="Enter your email"
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                className="w-full py-2.5 sm:py-3 pl-3 sm:pl-4 pr-8 sm:pr-10 rounded-lg sm:rounded-xl bg-gray-100 text-sm sm:text-base outline-none border-none mb-1 placeholder-gray-400"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                {/* email icon svg */}
                <svg width="18" height="18" viewBox="0 0 32 22" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400"><rect x="1.5" y="3.5" width="29" height="15" rx="2.5" stroke="currentColor" strokeWidth="2"/><path d="M3 5L16 15L29 5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>
              </span>
            </div>
            <label className="text-sm sm:text-base font-semibold mb-1 mt-2 text-gray-800">Password</label>
            <div className="relative mb-3">
              <input
                type="password"
                placeholder="Enter your password"
                value={loginPassword}
                onChange={e => setLoginPassword(e.target.value)}
                className="w-full py-2.5 sm:py-3 pl-3 sm:pl-4 pr-8 sm:pr-10 rounded-lg sm:rounded-xl bg-gray-100 text-sm sm:text-base outline-none border-none mb-1 placeholder-gray-400"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                {/* password icon svg */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 sm:w-5 sm:h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V7.125A4.125 4.125 0 008.25 7.125V10.5m8.25 0a2.25 2.25 0 012.25 2.25v3.375a2.25 2.25 0 01-2.25 2.25H7.5a2.25 2.25 0 01-2.25-2.25V12.75a2.25 2.25 0 012.25-2.25m8.25 0h-8.25" /></svg>
              </span>
            </div>
            <div className="flex items-center justify-between mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
                />
                <span className="text-sm text-gray-700">Remember me</span>
              </label>
              <button
                type="button"
                className="text-blue-500 text-sm hover:underline"
                onClick={handleForgotPassword}
                disabled={loading}
              >
                Forgot Password?
              </button>
            </div>
            {resetPasswordSent && (
              <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-xl">
                Password reset email sent! Check your inbox.
              </div>
            )}
            {magicLinkSent && (
              <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-xl">
                Magic link sent! Check your inbox to continue signing in.
              </div>
            )}
            <button 
              type="submit" 
              disabled={loading || loginSuccessful}
              className="w-full py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-black text-white text-base sm:text-lg font-semibold mt-2 hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loginSuccessful ? 'Redirecting...' : loadingAction === 'login' ? 'Logging in...' : 'Login'}
            </button>
            <button
              type="button"
              disabled={loading || loginSuccessful}
              className="w-full py-2.5 sm:py-3 rounded-lg sm:rounded-xl border border-gray-300 text-gray-800 text-base sm:text-lg font-semibold mt-3 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleMagicLink}
            >
              {loadingAction === 'magic-link' ? 'Sending magic link...' : 'Send Magic Link'}
            </button>
            <button
              type="button"
              disabled={loading || loginSuccessful}
              className="w-full py-2.5 sm:py-3 rounded-lg sm:rounded-xl border border-gray-300 text-gray-800 text-base sm:text-lg font-semibold mt-3 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleGoogleSignIn}
            >
              {loadingAction === 'google' ? 'Redirecting to Google...' : 'Continue with Google'}
            </button>
            <p className="mt-3 text-center text-xs text-gray-500">
              Magic link sign-in is available for existing accounts. Use Sign Up if you need a new account.
            </p>
          </form>
        )}
        {activeTab === 'signup' && (
          <form className="w-full flex flex-col" onSubmit={handleSignup}>
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-xl">{error}</div>
            )}
            <label className="text-sm sm:text-base font-semibold mb-1 mt-2 text-gray-800">Name</label>
            <div className="relative mb-3">
              <input
                type="text"
                placeholder="Your full name"
                value={signupName}
                onChange={e => setSignupName(e.target.value)}
                className="w-full py-2.5 sm:py-3 pl-3 sm:pl-4 pr-8 sm:pr-10 rounded-lg sm:rounded-xl bg-gray-100 text-sm sm:text-base outline-none border-none mb-1 placeholder-gray-400"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                {/* user icon svg */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 sm:w-5 sm:h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>
              </span>
            </div>
            <label className="text-sm sm:text-base font-semibold mb-1 mt-2 text-gray-800">Email</label>
            <div className="relative mb-3">
              <input
                type="email"
                placeholder="Enter your email"
                value={signupEmail}
                onChange={e => setSignupEmail(e.target.value)}
                className="w-full py-2.5 sm:py-3 pl-3 sm:pl-4 pr-8 sm:pr-10 rounded-lg sm:rounded-xl bg-gray-100 text-sm sm:text-base outline-none border-none mb-1 placeholder-gray-400"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                {/* email icon svg */}
                <svg width="18" height="18" viewBox="0 0 32 22" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400"><rect x="1.5" y="3.5" width="29" height="15" rx="2.5" stroke="currentColor" strokeWidth="2"/><path d="M3 5L16 15L29 5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>
              </span>
            </div>
            <label className="text-sm sm:text-base font-semibold mb-1 mt-2 text-gray-800">Password</label>
            <div className="relative mb-3">
              <input
                type="password"
                placeholder="Create a password"
                value={signupPassword}
                onChange={e => setSignupPassword(e.target.value)}
                className="w-full py-2.5 sm:py-3 pl-3 sm:pl-4 pr-8 sm:pr-10 rounded-lg sm:rounded-xl bg-gray-100 text-sm sm:text-base outline-none border-none mb-1 placeholder-gray-400"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                {/* password icon svg */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 sm:w-5 sm:h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V7.125A4.125 4.125 0 008.25 7.125V10.5m8.25 0a2.25 2.25 0 012.25 2.25v3.375a2.25 2.25 0 01-2.25 2.25H7.5a2.25 2.25 0 01-2.25-2.25V12.75a2.25 2.25 0 012.25-2.25m8.25 0h-8.25" /></svg>
              </span>
            </div>
            <label className="text-sm sm:text-base font-semibold mb-1 mt-2 text-gray-800">Business Name</label>
            <div className="relative mb-3">
              <input
                type="text"
                placeholder="Your business name"
                value={signupBusiness}
                onChange={e => setSignupBusiness(e.target.value)}
                className="w-full py-2.5 sm:py-3 pl-3 sm:pl-4 pr-8 sm:pr-10 rounded-lg sm:rounded-xl bg-gray-100 text-sm sm:text-base outline-none border-none mb-1 placeholder-gray-400"
              />
              <span className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                {/* business icon svg */}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 sm:w-5 sm:h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18m9-9H3" /></svg>
              </span>
            </div>
             <label className="text-sm sm:text-base font-semibold mb-1 mt-2 text-gray-800">Phone Number</label>
             <div className="relative mb-3">
               <input
                 type="tel"
                 placeholder="e.g. (555) 123-4567"
                 value={signupPhone}
                 onChange={e => setSignupPhone(e.target.value)}
                 className="w-full py-2.5 sm:py-3 pl-3 sm:pl-4 pr-8 sm:pr-10 rounded-lg sm:rounded-xl bg-gray-100 text-sm sm:text-base outline-none border-none mb-1 placeholder-gray-400"
               />
               <span className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                  {/* phone icon svg */}
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 sm:w-5 sm:h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" /></svg>
               </span>
             </div>
             <label className="text-sm sm:text-base font-semibold mb-1 mt-2 text-gray-800">Referral Code (Optional)</label>
             <div className="relative mb-3">
               <input
                 type="text"
                 placeholder="Have a referral code? Enter it here"
                 value={signupReferralCode}
                 onChange={e => setSignupReferralCode(e.target.value.toUpperCase())}
                 className="w-full py-2.5 sm:py-3 pl-3 sm:pl-4 pr-8 sm:pr-10 rounded-lg sm:rounded-xl bg-gray-100 text-sm sm:text-base outline-none border-none mb-1 placeholder-gray-400 font-mono"
               />
               <span className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 sm:w-5 sm:h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" /></svg>
               </span>
             </div>
             <div className="text-xs text-gray-400 mb-2">By signing up, you agree to our Terms of Service and Privacy Policy.</div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-black text-white text-base sm:text-lg font-semibold mt-2 hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingAction === 'signup' ? 'Creating Account...' : 'Create Account'}
            </button>
            {signupSuccess && (
              <div className="text-sm text-green-600 mt-2 text-center p-3 bg-green-50 border border-green-200 rounded-xl">
                <div className="font-semibold">Account created successfully!</div>
                <div className="mt-1">Please check your email and click the confirmation link to activate your account.</div>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default Auth;
